<template>
  <VMultiModal :showing="showing && !!folderId" data-intercom-target="create-pdf-modal">
    <EditorDeckModalsLocalOrServerChoice
      v-if="activeDialog === AddPdfSteps.CHOOSE_SOURCE"
      :key="AddPdfSteps.CHOOSE_SOURCE"
      showing
      :company-id="deck.company_id"
      :folder-id="folderId"
      file-type="PDF"
      mime-type="application/pdf"
      @close="close"
      @choose-file="chooseFile"
      @goto:shelf="activeDialog = AddPdfSteps.USE_SHELF"
    />
    <EditorDeckModalsShelfSelector
      v-if="activeDialog === AddPdfSteps.USE_SHELF"
      :key="refreshKey"
      showing
      :folder-id="folderId"
      :current-file="state.file"
      :company-id="deck.company_id"
      file-type="PDF"
      mime-type="application/pdf"
      @choose-file="chooseFiles($event)"
      @close="close"
      @back="activeDialog = AddPdfSteps.CHOOSE_SOURCE"
    />
    <EditorDeckModalsReplacePages
      v-if="activeDialog === AddPdfSteps.REPLACE_PAGES"
      :key="AddPdfSteps.REPLACE_PAGES"
      showing
      :slides-to-replace="slidesToReplace"
      :pdf-file="state.file"
      @close="close"
      @bulk-replace-pdf-source="replaceSlides"
      @back="activeDialog = AddPdfSteps.CHOOSE_SOURCE"
    />
    <EditorDeckModalsSelectPages
      v-if="activeDialog === AddPdfSteps.SELECT_PAGES"
      :key="AddPdfSteps.SELECT_PAGES"
      showing
      :pdf-file="state.file"
      @close="close"
      @choose-pages="choosePages($event)"
      @back="activeDialog = AddPdfSteps.USE_SHELF"
    />
  </VMultiModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { reactive, ref, toRef, watch } from 'vue'

import type { ShelfFile } from '@/entities/shelfFile'
import type { EditDeck } from '@/composables/useEditDeck'
import type { Deck, IncludeCompany, Slide } from '@/entities/deck'

enum AddPdfSteps {
  CHOOSE_SOURCE,
  USE_SHELF,
  SELECT_PAGES,
  REPLACE_PAGES,
}

const props = defineProps({
  showing: {
    type: Boolean,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  folderId: {
    type: Number,
    default: null,
  },
  slidesToReplace: {
    type: Array as PropType<Slide[] | null>,
    default: () => [],
  },
})

const emit = defineEmits(['update:showing', 'add-slides', 'bulk-replace-pdf-source'])

const activeDialog = ref<AddPdfSteps>(AddPdfSteps.CHOOSE_SOURCE)
const state = reactive({
  file: null as ShelfFile | null,
  pages: [] as number[],
})

// so we can clear the ShelfSelector state when a new PDF is uploaded
const refreshKey = ref(0)

watch(toRef(props, 'showing'), async showing => {
  if (showing) {
    activeDialog.value = AddPdfSteps.CHOOSE_SOURCE
  }
})

function chooseFile(file: ShelfFile) {
  chooseFiles(file)
  refreshKey.value++
}

function chooseFiles(file: ShelfFile) {
  state.file = file
  if (!!props.slidesToReplace?.length) {
    activeDialog.value = AddPdfSteps.REPLACE_PAGES
  } else {
    activeDialog.value = AddPdfSteps.SELECT_PAGES
  }
}

function choosePages(pages: any) {
  state.pages = pages
  submit()
}

function replaceSlides(slides: Partial<Slide>[]) {
  emit('bulk-replace-pdf-source', slides)

  close()
}

async function submit() {
  if (!state.file || state.pages.length === 0) return

  emit(
    'add-slides',
    state.pages.map(page => ({
      type: 'P',
      deck_id: props.deck.id,
      file_id: state.file?.id,
      page_number: page,
      title: state.pages.length === 1 ? state.file!.name : `${state.file!.name} - Page ${page}`,
      description: null,
    }))
  )

  close()
}

function close() {
  state.file = null
  state.pages = []
  emit('update:showing', false)
}
</script>
